/**
 * Resource Categories sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchResourceCategories,
  fetchResourceCategory,
  createResourceCategory,
  updateResourceCategoryAction,
  deleteResourceCategory,
  setCategories,
  setCurrentCategory,
  addCategory,
  updateCategory,
  removeCategory,
  setLoading,
  setError,
} from './resourceCategoriesSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all resource categories saga
 */
function* fetchResourceCategoriesSaga(): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.resourceCategories.getAll);

    if (response?.success) {
      yield put(setCategories(response.data || response));
    } else {
      toast.error('Failed to fetch resource categories');
      yield put(setError('Failed to fetch resource categories'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch resource categories';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single resource category saga
 */
function* fetchResourceCategorySaga(action: ReturnType<typeof fetchResourceCategory>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.resourceCategories.getById(action.payload));

    if (response?.success) {
      yield put(setCurrentCategory(response.data || response));
    } else {
      toast.error('Failed to fetch resource category');
      yield put(setError('Failed to fetch resource category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch resource category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create resource category saga
 */
function* createResourceCategorySaga(action: ReturnType<typeof createResourceCategory>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.resourceCategories.create, action.payload);

    if (response?.success) {
      const newCategory = response.data || response;
      yield put(addCategory(newCategory));
    } else {
      toast.error('Failed to create resource category');
      yield put(setError('Failed to create resource category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to create resource category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update resource category saga
 */
function* updateResourceCategorySaga(action: ReturnType<typeof updateResourceCategoryAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.resourceCategories.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedCategory = { ...response.data, _id: action.payload.id };
      yield put(updateCategory(updatedCategory));
    } else {
      toast.error('Failed to update resource category');
      yield put(setError('Failed to update resource category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to update resource category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete resource category saga
 */
function* deleteResourceCategorySaga(action: ReturnType<typeof deleteResourceCategory>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.resourceCategories.delete(action.payload));

    if (response?.success || response) {
      yield put(removeCategory(action.payload));
    } else {
      toast.error('Failed to delete resource category');
      yield put(setError('Failed to delete resource category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to delete resource category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* resourceCategoriesSagas(): Generator<any, void, any> {
  yield takeLatest(fetchResourceCategories, fetchResourceCategoriesSaga);
  yield takeLatest(fetchResourceCategory, fetchResourceCategorySaga);
  yield takeLatest(createResourceCategory, createResourceCategorySaga);
  yield takeLatest(updateResourceCategoryAction, updateResourceCategorySaga);
  yield takeLatest(deleteResourceCategory, deleteResourceCategorySaga);
}

